import { AssetSetType } from '@droidmap/shared-service-contract'

/* -------------------------- AssetSet Operations --------------------------- */

/**
 * What an asset-set operation does to the assets it names.
 *
 * - `delete` removes assets from their set (objects, thumbnails and rows).
 * - `copy` duplicates assets into a target set. The source is untouched and
 *   the copies are new assets with new ids — they add real bytes to the org's
 *   storage. Cloning a whole set is a `copy` with no `assetIds`.
 * - `move` relocates assets into a target set, keeping their ids. Splitting a
 *   set is one `move` per new set.
 */
export enum AssetSetOperationType {
  Delete = 'delete',
  Copy = 'copy',
  Move = 'move',
}

export enum AssetSetOperationStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  /**
   * The operation stopped. Assets already processed stay processed — an
   * operation is resumable per asset, not transactional as a whole — so the
   * rollups on both sets reflect exactly the work that was done.
   */
  Failed = 'failed',
}

/**
 * Asset set types whose membership may be edited (delete / move out / copy or
 * move in).
 *
 * Output sets are excluded: an `odm_outputs` set carries a `manifest.json`
 * listing its asset ids and a task whose `viewableAssets` snapshot points at
 * them, and neither is rewritten when membership changes. They can still be
 * the *source* of a copy — a copy leaves the source intact.
 */
export const EDITABLE_ASSET_SET_TYPES: ReadonlySet<AssetSetType> = new Set([
  AssetSetType.DroneImages,
  AssetSetType.Lidar,
  AssetSetType.MapLayers,
  AssetSetType.Other,
])

/** Whether assets may be removed from, or added to, a set of this type. */
export const isEditableAssetSetType = (assetSetType: AssetSetType): boolean =>
  EDITABLE_ASSET_SET_TYPES.has(assetSetType)

/**
 * Upper bound on explicitly named assets per operation. Well above the largest
 * real drone set, and small enough that the id list fits comfortably in the
 * request body and the job row.
 */
export const MAX_ASSET_SET_OPERATION_ASSET_IDS = 20_000

/** Where a copy or move puts its assets. */
export type AssetSetOperationTarget =
  | {
      /** An existing set in the same project as the source. */
      assetSetId: string
      name?: never
    }
  | {
      /**
       * Create a new set with this name, in the source's project and of the
       * source's type, and use it as the target.
       */
      name: string
      assetSetId?: never
    }

export type CreateAssetSetOperationInput = {
  /** The set the assets currently belong to. (ulid) */
  sourceAssetSetId: string
  type: AssetSetOperationType
  /**
   * The assets to act on, all of which must belong to the source set. Omit to
   * act on every asset in the set — allowed for `copy` (clone) and `move`
   * (merge into another set), not for `delete`, which is what deleting the set
   * is for.
   */
  assetIds?: string[]
  /** Required for `copy` and `move`; must be absent for `delete`. */
  target?: AssetSetOperationTarget
}

/**
 * An asynchronous bulk operation over the assets of one asset set.
 *
 * Created by `POST /asset-sets/:id/operations`, processed from the asset
 * event queue, and polled via `GET /asset-set-operations/:id`.
 */
export type AssetSetOperation = {
  /** (ulid) */
  id: string
  type: AssetSetOperationType
  status: AssetSetOperationStatus
  projectId: string
  sourceAssetSetId: string
  /** Set for `copy` and `move`. For a new-set target, the set created for it. */
  targetAssetSetId?: string
  /**
   * How many assets the operation processes. The ids themselves are kept
   * server-side — a whole-set operation snapshots every id in the set, which
   * is too large to return on each poll or carry on an event.
   */
  totalAssets: number
  /** Assets processed so far, including ones skipped as already done. */
  processedAssets: number
  /** Assets that could not be processed. */
  failedAssets: number
  /** The first failures, capped, for display. */
  failedAssetIds?: string[]
  errorMessage?: string
  created: Date
  createdBy: string
  updated: Date
  updatedBy: string
}

export type GetAssetSetOperationInput = {
  operationId: string
}
